import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import RequestCard from "../components/RequestCard";
import { getCategories, getRequests, getMyRequests } from "../utils/api";

function normCat(c) {
  return {
    id: c?.id,
    title: c?.title ?? c?.label ?? "Category",
    label: c?.label ?? c?.title ?? "Category",
    image_path: c?.image_path ?? c?.image ?? "",
  };
}

export default function Requests({ currentUser, token }) {
  const role = currentUser?.role || "";
  const isAdmin = role === "admin";
  const isSeller = role === "seller" || isAdmin;
  const isBuyer = role === "buyer";

  const canCreateRequest = isBuyer || isAdmin;

  const [categories, setCategories] = useState([]);
  const [requests, setRequests] = useState([]);

  const [status, setStatus] = useState("open"); // open | fulfilled | closed
  const [catId, setCatId] = useState("all");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // load categories
  useEffect(() => {
    let cancelled = false;

    async function loadCats() {
      try {
        const cats = await getCategories();
        if (cancelled) return;
        setCategories(Array.isArray(cats) ? cats.map(normCat) : []);
      } catch {
        if (!cancelled) setCategories([]);
      }
    }

    loadCats();
    return () => {
      cancelled = true;
    };
  }, []);

  // load requests (seller/admin: board; buyer: my requests)
  useEffect(() => {
    let cancelled = false;

    async function loadReqs() {
      try {
        setLoading(true);
        setError("");

        let list = [];

        if (isSeller) {
          // seller/admin sees the community board by status
          list = await getRequests(status);
        } else if (isBuyer) {
          // buyer sees only their own requests 
          if (!token) {
            list = [];
            setError("Please log in to view your requests.");
          } else {
            list = await getMyRequests(token);
          }
        } else {
          // guest or unknown role: show open board (view-only)
          list = await getRequests(status);
        }

        if (cancelled) return;
        setRequests(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) {
          setRequests([]);
          setError(String(e?.message || "Failed to load requests"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadReqs();
    return () => {
      cancelled = true;
    };
  }, [status, isSeller, isBuyer, token]);

  // client-side filters
  let filtered = Array.isArray(requests) ? requests : [];

  // For buyers: /my-requests returns all statuses, so we filter by status here
  if (isBuyer) {
    filtered = filtered.filter(
      (r) => String(r?.status || "open") === String(status),
    );
  }

  if (catId !== "all") {
    filtered = filtered.filter(
      (r) => String(r?.product_category_id || "") === String(catId),
    );
  }

  return (
    <div className="container pagePadBottom reqPage">
      <div className="reqHeader">
        <div>
          <div className="reqTitle">
            {isBuyer ? "My Requests" : "Buyer Requests"}
          </div>
          <div className="reqSub muted">
            {isBuyer
              ? "Create and manage your requests."
              : "Community board — see what people need right now."}
          </div>
        </div>

        <div className="reqHeadRight">
          <div className="pill">
            {filtered.length} <span className="pillStrong">requests</span>
          </div>

          {canCreateRequest ? (
            <Link to="/add-request" className="btnPrimary">
              + Add Request
            </Link>
          ) : null}
        </div>
      </div>

      <div className="reqControls">
        <div className="reqTabs">
          {["open", "fulfilled", "closed"].map((s) => (
            <button
              key={s}
              type="button"
              className={status === s ? "reqTab reqTabActive" : "reqTab"}
              onClick={() => setStatus(s)}
            >
              {s}
            </button>
          ))}
        </div>

        <div className="reqFilter">
          <div className="label">Category</div>
          <select
            className="input"
            value={catId}
            onChange={(e) => setCatId(e.target.value)}
          >
            <option value="all">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="muted" style={{ marginTop: 12 }}>
          Loading…
        </div>
      ) : error ? (
        <div className="card" style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 900 }}>Cannot load requests</div>
          <div className="muted" style={{ marginTop: 6 }}>
            {error}
          </div>
          <div className="row" style={{ marginTop: 12, flexWrap: "wrap" }}>
            <Link className="btnPrimary" to="/login">
              Login
            </Link>
            <Link className="btnSecondary" to="/categories">
              Browse items
            </Link>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="emptyBox" style={{ marginTop: 12 }}>
          <div className="emptyTitle">No requests found</div>
          <div className="muted small" style={{ marginTop: 6 }}>
            Try changing status or category.
          </div>
        </div>
      ) : (
        <div className="reqGrid reqGridList" style={{ marginTop: 12 }}>
          {filtered.map((r) => (
            <RequestCard
              key={r.id}
              request={r}
              categories={categories}
              actionTo={`/requests/${r.id}`}
              actionLabel="View Request →"
            />
          ))}
        </div>
      )}
    </div>
  );
}
